import * as React from 'react';

import { translate } from '~l10n/client';
import { useSetAtom } from 'jotai';

import { ConfirmTransform } from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { showConfirmAtom } from '~teacher/components/rooms/ShareRoomDialog/ShareRoomDialog';
import { useCreateRoomShareLinkMutation } from '~teacher/hook/room';
import { useRoomDetail } from '~teacher/hook/room/useRoomDetail';

interface Props {
  roomId: number;
}

interface UseHandleResetLinkParams {
  roomName: string;
  roomId: number;
  closeConfirmDialog: () => void;
}

function useHandleResetLink({
  roomName,
  roomId,
  closeConfirmDialog,
}: UseHandleResetLinkParams) {
  const createRoomShareLinkMutation = useCreateRoomShareLinkMutation();
  const [isResettingLink, setIsResettingLink] = React.useState(false);

  const handleResetLink = React.useCallback(async () => {
    setIsResettingLink(true);

    createRoomShareLinkMutation.mutate(
      { roomName, roomId },
      {
        onSuccess: async () => {
          closeConfirmDialog();
          toast(translate('Share link reset'));
        },
        onError: () => {
          setIsResettingLink(false);
          toast.critical(translate('An error has occurred'));
        },
      }
    );
  }, [closeConfirmDialog, createRoomShareLinkMutation, roomId, roomName]);
  return { isResettingLink, handleResetLink };
}

export function ShareRoomDialogConfirm({ roomId }: Props) {
  const roomDetail = useRoomDetail({
    roomId: roomId,
  });

  const setShowConfirm = useSetAtom(showConfirmAtom);
  const closeConfirmDialog = () => setShowConfirm(false);

  const { isResettingLink, handleResetLink } = useHandleResetLink({
    roomName: roomDetail.data?.name,
    roomId: roomId,
    closeConfirmDialog: closeConfirmDialog,
  });

  return (
    <ConfirmTransform
      title={translate('Reset Share Link?')}
      onClose={closeConfirmDialog}
      confirm={translate('Reset Share Link')}
      description={translate(
        'Students will no longer be able to use the old Room link and QR code.'
      )}
      action={handleResetLink}
      isLoading={isResettingLink}
    />
  );
}
